/**
 * @file     DealFinderClient.tsx
 * @purpose  Client-side parts of the deal-finder page: a stable header (it
 *           never animates, see the deal-finder rules) that names the chosen
 *           journey, the returning-visitor banner and the UnifiedLeadFlow. The
 *           flow is client-only, so the server HTML carries the H1 and a
 *           skeleton in its place.
 */
"use client";

import { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import dynamic from "next/dynamic";
import { useTranslation } from "react-i18next";
import { headingFont, hubColors } from "@/components/hub/hubKitStyles";
import { hubTypographyTokens } from "@/design-system/tokens/typography";
import type { LeadBranch } from "@/features/unified-lead-flow/flowPersistence";
import { ReturningVisitorBanner } from "@/features/deal-finder/ReturningVisitorBanner";

const LeadFlowSkeleton = () => (
  <Box
    data-testid="lead-flow-skeleton"
    sx={{ display: "flex", flexDirection: "column", gap: 2, minHeight: 480 }}
  >
    <Skeleton variant="rounded" height={38} width="40%" />
    <Skeleton variant="rounded" height={20} width="70%" />
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1 }}>
      {[0, 1, 2].map((i) => (
        <Skeleton
          key={i}
          variant="rounded"
          height={104}
          sx={{ flex: "1 1 200px" }}
        />
      ))}
    </Box>
  </Box>
);

const UnifiedLeadFlow = dynamic(
  () =>
    import("@/features/unified-lead-flow/UnifiedLeadFlow").then(
      (m) => m.UnifiedLeadFlow,
    ),
  { ssr: false, loading: () => <LeadFlowSkeleton /> },
);

type DealFinderClientProps = {
  sourcePage: string;
  defaultBranch?: LeadBranch;
};

export const DealFinderClient = ({ sourcePage, defaultBranch }: DealFinderClientProps) => {
  const { t } = useTranslation();
  const [branch, setBranch] = useState<LeadBranch | null>(null);
  const isDedicatedFlow = defaultBranch !== undefined;

  const content = useMemo(() => {
    switch (branch) {
      case "seller":
        return {
          title: t("dealFinder.header.seller.title", { defaultValue: "Find out what your home is worth" }),
          description: t("dealFinder.header.seller.description", { defaultValue: "Answer a few questions about your home and get a value range. It is free." }),
        };
      case "landlord":
        return {
          title: t("dealFinder.header.landlord.title", { defaultValue: "Find quality tenants" }),
          description: t("dealFinder.header.landlord.description", { defaultValue: "Share your rental's details and what you want in a tenant." }),
        };
      default:
        return {
          title: t("dealFinder.header.shortlist.title", { defaultValue: "Get a shortlist of homes that fit" }),
          description: t("dealFinder.header.shortlist.description", { defaultValue: "Pick what you want to do and answer a few questions." }),
        };
    }
  }, [branch, t]);

  const h1 = hubTypographyTokens.h1;

  return (
    <Box
      component="section"
      sx={{
        pt: { xs: 6, md: 10 },
        pb: { xs: 10, md: 14 },
        px: { xs: 5, md: 6 },
        maxWidth: 728,
        mx: "auto",
        color: hubColors.textPrimary,
      }}
    >
      <ReturningVisitorBanner />

      {!isDedicatedFlow && (
        <Box sx={{ mb: { xs: 5, md: 6 } }}>
          <Box
            component="h1"
            sx={{
              m: 0,
              fontFamily: headingFont,
              fontSize: { xs: 32, md: 44 },
              fontWeight: h1.fontWeight,
              lineHeight: 1.1,
              letterSpacing: h1.letterSpacing,
              textWrap: "balance",
            }}
          >
            {content.title}
          </Box>
          {content.description && (
            <Box
              component="p"
              sx={{ m: 0, mt: 3, fontSize: { xs: 16, md: 18 }, lineHeight: 1.5, color: hubColors.textSecondary }}
            >
              {content.description}
            </Box>
          )}
        </Box>
      )}

      <UnifiedLeadFlow sourcePage={sourcePage} defaultBranch={defaultBranch} onBranchChange={setBranch} />
    </Box>
  );
};
